import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "usl_admin_token";
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ""}` });

export default function Watchers() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("Active");

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/watch/subscribers`, { headers: authHeader() }); setItems(data); }
    catch { toast.error("Failed to load."); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id, email) => {
    if (!window.confirm(`Delete watcher ${email}?`)) return;
    try { await axios.delete(`${API}/admin/watch/subscribers/${id}`, { headers: authHeader() }); load(); }
    catch { toast.error("Failed"); }
  };

  const filtered = filter === "All" ? items : items.filter(w => filter === "Active" ? w.active : !w.active);

  const exportCsv = () => {
    const header = ["name", "email", "postcode", "region", "frequency", "interests", "notes", "active", "created_at"];
    const rows = filtered.map(w => header.map(h => {
      const v = h === "interests" ? (w.interests || []).join(" | ") : w[h];
      return `"${String(v ?? "").replace(/"/g, '""')}"`;
    }).join(","));
    const blob = new Blob([[header.join(","), ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `upsurge-watchers-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="overline mr-2">Show:</div>
        {["Active", "Inactive", "All"].map(s => (
          <button key={s} data-testid={`watch-filter-${s}`} onClick={() => setFilter(s)}
            className={`text-[11px] tracking-[0.18em] uppercase font-semibold border px-3 py-1.5 ${filter === s ? "border-[#C9A24B] bg-[#C9A24B]/10 text-[#C9A24B]" : "border-[#F4EFE6]/15 text-[#9BA39B] hover:border-[#C9A24B]/50"}`}
          >{s}</button>
        ))}
        <div className="ml-auto">
          <button data-testid="watch-export" onClick={exportCsv} className="usl-btn text-xs"><Download size={14} /> Export CSV</button>
        </div>
      </div>

      <div className="border border-[#F4EFE6]/10 bg-[#11171A]">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 text-[10px] tracking-[0.24em] uppercase text-[#9BA39B] font-semibold border-b border-[#F4EFE6]/10">
          <div className="col-span-3">Name / Email</div>
          <div className="col-span-2">Postcode</div>
          <div className="col-span-2">Region</div>
          <div className="col-span-1">Freq</div>
          <div className="col-span-2">Interests</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1"></div>
        </div>
        {filtered.length === 0 ? (
          <div data-testid="watch-empty" className="p-10 text-center text-[#9BA39B] text-sm">No watchers yet.</div>
        ) : filtered.map(w => (
          <div key={w.id} data-testid={`watch-row-${w.id}`} className="grid grid-cols-12 gap-3 px-5 py-4 items-center text-sm border-b border-[#F4EFE6]/10">
            <div className="col-span-3">
              <div className="text-[#F4EFE6] font-semibold">{w.name}</div>
              <div className="text-[11px] text-[#9BA39B]">{w.email}</div>
            </div>
            <div className="col-span-2 text-[#E8E1D2] uppercase">{w.postcode}</div>
            <div className="col-span-2 text-[#9BA39B] text-[12px]">{w.region || "—"}</div>
            <div className="col-span-1 text-[#C9A24B] text-[11px] uppercase tracking-wider">{w.frequency}</div>
            <div className="col-span-2 text-[#9BA39B] text-[11px]">{(w.interests || []).join(", ") || "—"}</div>
            <div className="col-span-1">
              <span className={`text-[10px] tracking-[0.22em] uppercase font-semibold border px-2 py-0.5 ${w.active ? "border-emerald-400/40 text-emerald-300" : "border-[#9BA39B]/40 text-[#9BA39B]"}`}>{w.active ? "Active" : "Off"}</span>
            </div>
            <div className="col-span-1 text-right">
              <button data-testid={`watch-delete-${w.id}`} onClick={() => remove(w.id, w.email)} className="text-[#9BA39B] hover:text-rose-400 p-1.5"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#9BA39B] mt-4">When you&apos;re ready to start sending, export this list and merge into your email tool. Each subscriber has a one-click unsubscribe token stored in the database.</p>
    </div>
  );
}
